import { apiErrorMessage } from '@/lib/errors'
import type {
  LinearTaskBuildBucketKey,
  LinearTaskPullRequest,
  MergePullRequestsResponse,
  PrAutoFixStopReason,
  PullRequestActionQueueStatus,
  PullRequestReviewStatus,
  RuleFinding,
} from '@/types/models'

export const NEUTRAL_BADGE_CLASS = 'bg-surface-container-highest border-outline-variant/30 text-on-surface-variant'

export const HTTP_STATUS_TOO_MANY_REQUESTS = 429

// Labels/colors for the Builds page's own four section headers and summary
// badges. 'no_action_needed' and 'needs_you' are gone — every task the
// buckets endpoint returns now falls into exactly one of these four, mapping
// to what's actually happening rather than hiding it behind a vague status.
export const BUILD_BUCKET_LABELS: Record<LinearTaskBuildBucketKey, string> = {
  ready_to_merge: 'Ready to merge',
  fixing: 'Fixing',
  in_progress: 'In progress',
  failed: 'Failed builds',
}

export const BUILD_BUCKET_BADGE_CLASSES: Record<LinearTaskBuildBucketKey, string> = {
  ready_to_merge: 'bg-secondary-container/20 border-secondary-container/30 text-secondary-fixed',
  fixing: 'bg-tertiary-container/20 border-tertiary-container/30 text-tertiary',
  in_progress: NEUTRAL_BADGE_CLASS,
  failed: 'bg-error-container/20 border-error-container/30 text-error',
}

export const REVIEW_STATUS_LABELS: Record<PullRequestReviewStatus, string> = {
  running: 'Reviewing…',
  reviewed: 'Reviewed clean',
  changes_requested: 'Changes requested',
  failed: 'Review failed',
}

export const REVIEW_STATUS_BADGE_CLASSES: Record<PullRequestReviewStatus, string> = {
  running: 'bg-primary-container/20 border-primary-container/30 animate-pulse text-primary-fixed',
  reviewed: 'bg-secondary-container/20 border-secondary-container/30 text-secondary-fixed',
  changes_requested: 'bg-tertiary-container/20 border-tertiary-container/30 text-tertiary',
  failed: 'bg-error-container/20 border-error-container/30 text-error',
}

export const AUTO_FIX_STOP_REASON_LABELS: Record<PrAutoFixStopReason, string> = {
  max_attempts_reached: 'Auto-fix stopped — attempt cap reached',
  untrusted_author: 'Auto-fix skipped — author lacks write access',
  oscillation_detected: 'Auto-fix stopped — attempts were undoing each other',
}

// A finding with no rule is a plain observation (see RuleFinding's own
// backend docblock and RecordReviewFindingsAction) rather than a violation of
// a configured custom rule — the only severity-like split the review
// pipeline currently records per finding.
export function findingSeverityLabel(finding: RuleFinding): string {
  return finding.rule ? 'Rule violation' : 'Observation'
}

export function findingSeverityBadgeClass(finding: RuleFinding): string {
  return finding.rule
    ? 'bg-error-container/20 border-error-container/30 text-error'
    : NEUTRAL_BADGE_CLASS
}

export type LifecycleStage = 'opened' | 'review' | 'auto_fix' | 're_review' | 'ready'

export const LIFECYCLE_STAGES: LifecycleStage[] = ['opened', 'review', 'auto_fix', 're_review', 'ready']

export const LIFECYCLE_STAGE_LABELS: Record<LifecycleStage, string> = {
  opened: 'Opened',
  review: 'Review',
  auto_fix: 'Auto-fix',
  re_review: 'Re-review',
  ready: 'Ready',
}

/**
 * Where one PR sits in the Opened → Review → Auto-fix → Re-review → Ready
 * lifecycle — derived from review_status/auto_fix rather than a separately
 * tracked stage, so it can never disagree with the state it's read off of.
 *
 * attempts_used > 0 is what distinguishes a first review from a re-review:
 * the loop hasn't pushed a fix yet in the first case, so whatever review is
 * running or just concluded is the PR's first one.
 */
export function pullRequestLifecycleStage(pullRequest: LinearTaskPullRequest): LifecycleStage {
  const { review_status: reviewStatus, auto_fix: autoFix } = pullRequest

  if (reviewStatus === 'reviewed') return 'ready'
  if (autoFix?.status === 'review_in_flight') return 're_review'
  if (autoFix?.status === 'fix_in_flight') return 'auto_fix'
  if (reviewStatus === null) return 'opened'

  const hasFixedBefore = (autoFix?.attempts_used ?? 0) > 0

  if (reviewStatus === 'running') return hasFixedBefore ? 're_review' : 'review'

  // changes_requested or failed, with no fix currently running: either
  // about to claim its first auto-fix attempt, or already claimed one and
  // is still open after the review that followed it.
  return hasFixedBefore ? 're_review' : 'auto_fix'
}

/**
 * A multi-repo task's card shows one stepper for the whole group — pinned to
 * whichever of its PRs has made the least progress, since that's the one
 * still holding the ticket back.
 */
export function groupLifecycleStage(pullRequests: LinearTaskPullRequest[]): LifecycleStage {
  if (pullRequests.length === 0) return 'opened'

  return pullRequests
    .map(pullRequestLifecycleStage)
    .reduce((least, stage) => (LIFECYCLE_STAGES.indexOf(stage) < LIFECYCLE_STAGES.indexOf(least) ? stage : least))
}

export function bucketByActionQueueStatus<T>(
  items: T[],
  getStatus: (item: T) => PullRequestActionQueueStatus,
): { needsYou: T[]; readyToMerge: T[]; noActionNeeded: T[]; inProgress: T[] } {
  const needsYou: T[] = []
  const readyToMerge: T[] = []
  const noActionNeeded: T[] = []
  const inProgress: T[] = []

  for (const item of items) {
    switch (getStatus(item)) {
      case 'needs_you':
        needsYou.push(item)
        break
      case 'ready_to_merge':
        readyToMerge.push(item)
        break
      case 'no_action_needed':
        noActionNeeded.push(item)
        break
      default:
        inProgress.push(item)
    }
  }

  return { needsYou, readyToMerge, noActionNeeded, inProgress }
}

export interface MergeNotice {
  type: 'success' | 'error'
  message: string
}

/**
 * Turns a merge-endpoint response into the one-line banner shown after
 * clicking Merge. The endpoint is a snapshot, not the merge outcome — actual
 * merges land async via a queued job — so this only reports what got queued
 * versus skipped at request time, never "merged".
 */
export function describeMergeResult(result: MergePullRequestsResponse): MergeNotice {
  const queuedCount = result.queued_pull_requests.length
  const skippedCount = result.skipped_pull_requests.length

  if (queuedCount === 0) {
    return {
      type: 'error',
      message: 'No pull requests were queued to merge — they may have changed state since this page loaded.',
    }
  }

  const queuedLabel = `${queuedCount} pull request${queuedCount === 1 ? '' : 's'}`

  if (skippedCount === 0) {
    return { type: 'success', message: `Queued ${queuedLabel} to merge.` }
  }

  const skippedLabel = `${skippedCount} pull request${skippedCount === 1 ? '' : 's'}`
  return {
    type: 'success',
    message: `Queued ${queuedLabel} to merge. Skipped ${skippedLabel} no longer passing.`,
  }
}

// The merge endpoint is rate-limited (10/min per user) on top of the usual
// 403/404 ownership checks — worth a distinct message so a burst of clicks
// reads as "slow down" rather than a generic failure.
export function mergeErrorMessage(error: unknown): string {
  if (error && typeof error === 'object' && 'response' in error) {
    const response = (error as { response?: { status?: number } }).response
    if (response?.status === HTTP_STATUS_TOO_MANY_REQUESTS) {
      return 'Too many merge requests — wait a moment and try again.'
    }
  }
  return apiErrorMessage(error, 'Failed to merge pull requests.')
}
